import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Chatroom.css";
import { useMsal } from "@azure/msal-react";
import NavBar from "../components/NavBar/Navigation";
import Footer from "../components/Footer/Footer";
import bannerVid from "../assets/banner-video.mp4";

/* ─── Utility helpers ─────────────────────────────────── */
const STORAGE_KEY = "pg_chatrooms_v1";

const ROOMS = [
  { id: "general",       label: "General",       icon: "💬", description: "Open conversation for everyone" },
  { id: "announcements", label: "Announcements", icon: "📢", description: "Official updates from management" },
  { id: "projects",      label: "Projects",      icon: "🌿", description: "Discuss ongoing park projects" },
  { id: "social",        label: "Social",        icon: "🎉", description: "Off-topic, fun & social chat" },
  { id: "maintenance",   label: "Maintenance",   icon: "🔧", description: "Report and track maintenance tasks" },
];

const AVATAR_COLORS = [
  "#01696f","#437a22","#964219","#006494","#7a39bb",
  "#a13544","#da7101","#d19900","#0c4e54","#2e5c10",
];

function avatarColor(name = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name = "") {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}

function groupByDate(messages) {
  const groups = [];
  let lastLabel = null;
  messages.forEach(msg => {
    const label = formatDateLabel(msg.timestamp);
    if (label !== lastLabel) { groups.push({ type: "divider", label }); lastLabel = label; }
    groups.push({ type: "message", ...msg });
  });
  return groups;
}

/* ─── Persistent storage ──────────────────────────────── */
let _store = {};
try {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (raw) _store = JSON.parse(raw);
} catch (_) {}

function loadMessages(roomId) { return (_store[roomId] || []).slice(); }
function saveMessages(roomId, msgs) {
  _store[roomId] = msgs;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_store)); } catch (_) {}
}

/* ─── Avatar component ────────────────────────────────── */
function Avatar({ name, size = 36 }) {
  return (
    <div
      className="chat-avatar"
      style={{ width: size, height: size, background: avatarColor(name), fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
  );
}

/* ─── Single message bubble ───────────────────────────── */
function MessageBubble({ msg, isOwn, showAvatar, showName }) {
  return (
    <div className={`msg-row ${isOwn ? "msg-row--own" : "msg-row--other"}`}>
      {!isOwn && (
        <div className="msg-avatar-col">
          {showAvatar ? <Avatar name={msg.author} size={34} /> : <div style={{ width: 34 }} />}
        </div>
      )}
      <div className="msg-content">
        {showName && !isOwn && (
          <span className="msg-author" style={{ color: avatarColor(msg.author) }}>
            {msg.author}
          </span>
        )}
        <div className={`msg-bubble ${isOwn ? "msg-bubble--own" : "msg-bubble--other"}`}>
          <p>{msg.text}</p>
          <span className="msg-time">{formatTime(msg.timestamp)}</span>
        </div>
      </div>
      {isOwn && (
        <div className="msg-avatar-col">
          {showAvatar ? <Avatar name={msg.author} size={34} /> : <div style={{ width: 34 }} />}
        </div>
      )}
    </div>
  );
}

/* ─── Room sidebar item ───────────────────────────────── */
function RoomItem({ room, active, unread, onClick }) {
  return (
    <button
      className={`room-item ${active ? "room-item--active" : ""}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
    >
      <span className="room-icon">{room.icon}</span>
      <span className="room-label">{room.label}</span>
      {unread > 0 && <span className="room-badge">{unread > 99 ? "99+" : unread}</span>}
    </button>
  );
}

/* ─── Main Chatroom page ──────────────────────────────── */
export default function Chatroom() {
  const { accounts } = useMsal();                          // ← changed
  const account = accounts[0];                             // ← changed
  const user = account                                     // ← changed
    ? {
        fullName: account.idTokenClaims?.name || account.name || "User",
        role:     account.idTokenClaims?.jobTitle || account.idTokenClaims?.extension_Role || "Member",
      }
    : null;

  const [activeRoom, setActiveRoom] = useState(ROOMS[0].id);
  const [messagesByRoom, setMessagesByRoom] = useState(() => {
    const init = {};
    ROOMS.forEach(r => { init[r.id] = loadMessages(r.id); });
    return init;
  });
  const [draft, setDraft] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread, setUnread] = useState(() => {
    const u = {};
    ROOMS.forEach(r => { u[r.id] = 0; });
    return u;
  });

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const messages = messagesByRoom[activeRoom] || [];
  const currentRoom = ROOMS.find(r => r.id === activeRoom);

  useEffect(() => {
    setUnread(prev => ({ ...prev, [activeRoom]: 0 }));
  }, [activeRoom]);

  const switchRoom = useCallback((roomId) => {
    setActiveRoom(roomId);
    setSidebarOpen(false);
  }, []);

  const sendMessage = useCallback(() => {
    const text = draft.trim();
    if (!text || !user) return;
    const newMsg = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      author: user.fullName,
      text,
      timestamp: new Date().toISOString(),
      roomId: activeRoom,
    };
    setMessagesByRoom(prev => {
      const updated = { ...prev, [activeRoom]: [...(prev[activeRoom] || []), newMsg] };
      saveMessages(activeRoom, updated[activeRoom]);
      return updated;
    });
    setDraft("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [draft, user, activeRoom]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  if (!user) return null;

  const myName = user.fullName;
  const grouped = groupByDate(messages);

  return (
    <div className="chatroom-page">
      <div className="chat-banner">
        <video className="chat-banner-video" src={bannerVid} autoPlay loop muted playsInline />
        <NavBar />
        <div className="chat-banner-overlay">
          <div className="chat-banner-title">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-label="Chatroom" role="img">
              <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2z"
                fill="currentColor" opacity=".15"/>
              <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2z"
                stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <circle cx="10" cy="11" r="1.2" fill="currentColor"/>
              <circle cx="14" cy="11" r="1.2" fill="currentColor"/>
              <circle cx="18" cy="11" r="1.2" fill="currentColor"/>
            </svg>
            <span>Chatroom</span>
          </div>
          <p className="chat-banner-sub">Connect with your team — messages are saved automatically</p>
        </div>
      </div>

      <div className="chat-shell">
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(o => !o)}
          aria-label="Toggle room list"
          aria-expanded={sidebarOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="18" rx="1"/>
            <rect x="14" y="3" width="7" height="18" rx="1"/>
          </svg>
          <span>Rooms</span>
          {Object.values(unread).some(u => u > 0) && <span className="sidebar-toggle-dot" />}
        </button>

        <div className={`chat-layout ${sidebarOpen ? "chat-layout--sidebar-open" : ""}`}>
          <aside className="chat-sidebar" aria-label="Chat rooms">
            <div className="sidebar-header">
              <h2>Rooms</h2>
              <span className="sidebar-online-badge">
                <span className="online-dot" />
                Online
              </span>
            </div>
            <nav className="room-list" role="list">
              {ROOMS.map(room => (
                <RoomItem
                  key={room.id}
                  room={room}
                  active={room.id === activeRoom}
                  unread={unread[room.id] || 0}
                  onClick={() => switchRoom(room.id)}
                />
              ))}
            </nav>
            <div className="sidebar-footer">
              <Avatar name={myName} size={32} />
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{myName}</span>
                <span className="sidebar-user-role">{user.role}</span>
              </div>
            </div>
          </aside>

          <main className="chat-main" aria-label="Chat messages">
            <header className="chat-header">
              <div className="chat-header-left">
                <span className="chat-header-icon">{currentRoom?.icon}</span>
                <div>
                  <h1 className="chat-header-title">{currentRoom?.label}</h1>
                  <p className="chat-header-desc">{currentRoom?.description}</p>
                </div>
              </div>
              <div className="chat-header-right">
                <span className="chat-msg-count">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
              </div>
            </header>

            <div className="messages-area" ref={bottomRef} role="log" aria-live="polite" aria-label="Messages">
              {messages.length === 0 ? (
                <div className="chat-empty">
                  <div className="chat-empty-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="1.5" opacity=".2"/>
                      <path d="M14 16h20a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H18l-6 5V18a2 2 0 0 1 2-2z"
                        stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity=".5"/>
                      <circle cx="20" cy="23" r="1.5" fill="currentColor" opacity=".4"/>
                      <circle cx="24" cy="23" r="1.5" fill="currentColor" opacity=".4"/>
                      <circle cx="28" cy="23" r="1.5" fill="currentColor" opacity=".4"/>
                    </svg>
                  </div>
                  <h3>No messages yet</h3>
                  <p>Be the first to say something in <strong>{currentRoom?.label}</strong></p>
                </div>
              ) : (
                grouped.map((item, idx) => {
                  if (item.type === "divider") {
                    return <div key={`div-${idx}`} className="date-divider"><span>{item.label}</span></div>;
                  }
                  const isOwn = item.author === myName;
                  const prev = grouped[idx - 1];
                  const showAvatar = !prev || prev.type === "divider" || prev.author !== item.author;
                  return (
                    <MessageBubble
                      key={item.id}
                      msg={item}
                      isOwn={isOwn}
                      showAvatar={showAvatar}
                      showName={showAvatar}
                    />
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-bar">
              <div className="chat-input-inner">
                <Avatar name={myName} size={32} />
                <div className="chat-textarea-wrap">
                  <textarea
                    ref={inputRef}
                    className="chat-textarea"
                    placeholder={`Message #${currentRoom?.label.toLowerCase()}…`}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    aria-label="Type a message"
                    maxLength={2000}
                  />
                </div>
                <button
                  className={`chat-send-btn ${draft.trim() ? "chat-send-btn--active" : ""}`}
                  onClick={sendMessage}
                  disabled={!draft.trim()}
                  aria-label="Send message"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
              <p className="chat-input-hint">Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for new line</p>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}